import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useCurrentCompany, useCurrentUser, useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import { useNavbarPanelsStore } from '../../../hooks/use-navbar-panels';
import { resolvePageContext } from '../lib/bizy-page-config';
import { getBizyPanelPreference, hasTriggeredTrialAutoOpen, markTrialAutoOpenTriggered, setBizyPanelPreference } from '../lib/panel-preferences';
import { getAudienceSegment, isTrialSegment } from '../lib/suggested-prompts';
import { useBizyPageContextStore } from '../store/use-bizy-page-context-store';
import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';
import { useBizyPanelTracking } from './use-bizy-panel-tracking';

interface UseBizyLayoutOptions {
	isMobile: boolean;
	isExpanded: boolean;
	setIsExpanded: (expanded: boolean) => void;
}

export function handleBizySlashKeyDown(event: KeyboardEvent, togglePanel: () => void) {
	if (event.key !== '/' || event.repeat || event.metaKey || event.ctrlKey) return;

	const target = event.target as HTMLElement | null;
	const tag = target?.tagName;
	const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable;
	if (isEditable) return;

	event.preventDefault();
	togglePanel();
}

/**
 * Encapsulates all Bizy side-panel layout concerns so AppLayout stays clean.
 *
 * Handles: company gating, store subscriptions, route-based page context,
 * homepage auto-open, sidebar auto-collapse on medium screens,
 * and Bizy auto-collapse when navbar panels open.
 */
export function useBizyLayout({ isMobile, isExpanded, setIsExpanded }: UseBizyLayoutOptions) {
	const isBizyEnabledForUser = useIsBizyEnabledForUser();
	const location = useLocation();
	const company = useCurrentCompany();
	const currentUser = useCurrentUser();

	const isPanelOpen = useBizySidePanelStore((s) => s.isOpen);
	const isFullscreen = useBizySidePanelStore((s) => s.isFullscreen);
	const lastTrigger = useBizySidePanelStore((s) => s.lastTrigger);
	const panelWidth = useBizySidePanelStore((s) => s.width);
	const isResizing = useBizySidePanelStore((s) => s.isResizing);
	const togglePanel = useBizySidePanelStore((s) => s.toggle);
	const openPanel = useBizySidePanelStore((s) => s.open);
	const closePanel = useBizySidePanelStore((s) => s.close);
	const setPageContext = useBizyPageContextStore((s) => s.setContext);

	const isDesktop = isBizyEnabledForUser && !isMobile;
	const restoreOpenPanel = useCallback(() => openPanel('auto', { isHistoryDrawerOpen: false }), [openPanel]);

	// Memoize by pathname so the object identity is stable across renders.
	// Without this, `resolvePageContext` returns a fresh object each render,
	// which makes the sync effect below re-fire on every render and overwrite
	// any richer pageData set imperatively by a mounted page component (e.g.
	// OneOnOneView adds partner name and is_first_meeting to the store).
	const pageContext = useMemo(() => resolvePageContext(location.pathname), [location.pathname]);
	const isMeetingPage = pageContext.pageType === '1on1';
	const prevPageTypeRef = useRef<string | null>(null);
	const hasAppliedInitialPrefRef = useRef(false);

	const { trackPanelAutoOpened } = useBizyPanelTracking(isPanelOpen);

	const [isLargeDesktop, setIsLargeDesktop] = useState(() => {
		if (typeof window === 'undefined') return false;
		return window.matchMedia('(min-width: 1280px)').matches;
	});

	useEffect(() => {
		if (typeof window === 'undefined') return;
		const media = window.matchMedia('(min-width: 1280px)');
		const listener = (event: MediaQueryListEvent) => setIsLargeDesktop(event.matches);
		setIsLargeDesktop(media.matches);
		media.addEventListener('change', listener);

		return () => media.removeEventListener('change', listener);
	}, []);

	// Sync page context from current route
	useEffect(() => {
		if (!isBizyEnabledForUser || isMobile) return;
		setPageContext(pageContext);
	}, [isBizyEnabledForUser, isMobile, setPageContext, pageContext]);

	// Drop to side panel on cross-layout mount when fullscreen (e.g. arriving from admin).
	// On page refresh the store starts with isFullscreen: false, so this is a no-op.
	// Uses setState directly to preserve the user's fullscreen preference in localStorage.
	useEffect(() => {
		if (useBizySidePanelStore.getState().isFullscreen) {
			useBizySidePanelStore.setState({ isFullscreen: false, isHistoryDrawerOpen: false });
		}
	}, []);

	// Drop to side panel on within-layout route change when fullscreen.
	// Preserves the user's fullscreen preference so reopening restores fullscreen.
	const prevPathnameRef = useRef(location.pathname);
	useEffect(() => {
		const prev = prevPathnameRef.current;
		prevPathnameRef.current = location.pathname;
		if (prev !== location.pathname && useBizySidePanelStore.getState().isFullscreen) {
			useBizySidePanelStore.setState({ isFullscreen: false, isHistoryDrawerOpen: false });
		}
	}, [location.pathname]);

	// Route-aware auto-open/close based on localStorage preferences
	useEffect(() => {
		if (!isBizyEnabledForUser || isMobile) return;

		const currentPageType = pageContext.pageType;
		const prevPageType = prevPageTypeRef.current;
		prevPageTypeRef.current = currentPageType;

		const enteringMeeting = currentPageType === '1on1' && prevPageType !== '1on1';
		const leavingMeeting = prevPageType === '1on1' && currentPageType !== '1on1';
		const isAlreadyOpen = useBizySidePanelStore.getState().isOpen;

		if (enteringMeeting) {
			hasAppliedInitialPrefRef.current = true;
			if (getBizyPanelPreference('meeting') === 'open') {
				if (!isAlreadyOpen) restoreOpenPanel();
			} else closePanel('auto');
			return;
		}

		if (leavingMeeting) {
			hasAppliedInitialPrefRef.current = true;
			if (getBizyPanelPreference('general') === 'open') {
				if (!isAlreadyOpen) restoreOpenPanel();
			} else closePanel('auto');
			return;
		}

		// Initial page load: apply the relevant preference once
		if (!hasAppliedInitialPrefRef.current) {
			hasAppliedInitialPrefRef.current = true;
			const prefKey = currentPageType === '1on1' ? 'meeting' : 'general';
			if (getBizyPanelPreference(prefKey) === 'open') {
				// On general pages, only auto-open on large desktops (same as before)
				if (!isAlreadyOpen && (prefKey === 'meeting' || isLargeDesktop)) restoreOpenPanel();
			}
		}
	}, [isBizyEnabledForUser, isMobile, pageContext, isLargeDesktop, restoreOpenPanel, closePanel]);

	// Trial users landing on the homepage get the Bizy panel auto-opened once per
	// browser session, so the first thing they see is a guided entry point.
	// Desktop only; the sessionStorage flag is keyed per user to keep the trigger
	// stable across user switches in the same tab.
	const userId = currentUser?.id;
	// Derive trial status from the same audience-segment helper the chat view
	// uses, so the auto-open trigger and the trial welcome content can never
	// disagree about whether this is a trial company.
	const isTrial = isTrialSegment(getAudienceSegment(company?.status ?? '', currentUser?.admin ?? false));
	const isHomepage = pageContext.pageType === 'home';
	useEffect(() => {
		if (!userId) return;
		if (!isTrial) return;
		if (!isDesktop) return;
		if (!isHomepage) return;
		if (hasTriggeredTrialAutoOpen(userId)) return;

		// Mark the trigger as spent before the isOpen guard: if another effect
		// (e.g. the general-preference auto-open) already opened the panel this
		// render, the trial trigger is still logically "used" for this session.
		// Otherwise the flag would never persist and a later user-close +
		// navigation back to home would re-trigger auto-open, overriding intent.
		markTrialAutoOpenTriggered(userId);

		// Always fire the explicit auto-open analytics event when the trial
		// trigger fires this session — even if another effect already opened
		// the panel — so we can measure trial auto-open attribution distinct
		// from the generic ChatOpened event.
		trackPanelAutoOpened();

		if (useBizySidePanelStore.getState().isOpen) return;

		useBizySidePanelStore.getState().open('auto', { isFullscreen: false, isHistoryDrawerOpen: false });
	}, [userId, isTrial, isDesktop, isHomepage, trackPanelAutoOpened]);

	// Save preference when user manually closes or opens the panel
	const prevIsOpenRef = useRef(isPanelOpen);
	useEffect(() => {
		const wasOpen = prevIsOpenRef.current;
		prevIsOpenRef.current = isPanelOpen;
		if (wasOpen === isPanelOpen) return;
		// Persist for explicit user actions and notification-peek opens.
		// Including notification_peek means a peek-open survives a page
		// refresh (the user expects to find the panel where they left it).
		// The downside — peek-clicking a single notif may flip a previously
		// "closed" preference to "open" — is acceptable; users who never
		// want the panel can always close it again.
		if (lastTrigger !== 'user' && lastTrigger !== 'keyboard' && lastTrigger !== 'notification_peek') return;

		const prefKey = isMeetingPage ? 'meeting' : 'general';
		setBizyPanelPreference(prefKey, isPanelOpen ? 'open' : 'closed');
	}, [isPanelOpen, lastTrigger, isMeetingPage]);

	// Collapse the sidebar when Bizy needs the extra horizontal space. Restore
	// it only if Bizy collapsed an expanded sidebar in this session.
	const wasExpandedBeforeBizyRef = useRef<boolean | null>(null);
	const isSidebarCollapsedForBizyRef = useRef(false);
	useEffect(() => {
		if (isMobile) return;

		const shouldCollapseSidebar = isPanelOpen && (isFullscreen || !isLargeDesktop);

		if (shouldCollapseSidebar && !isSidebarCollapsedForBizyRef.current) {
			isSidebarCollapsedForBizyRef.current = true;
			wasExpandedBeforeBizyRef.current = isExpanded;
			if (isExpanded) setIsExpanded(false);
			return;
		}

		if (!shouldCollapseSidebar && isSidebarCollapsedForBizyRef.current) {
			isSidebarCollapsedForBizyRef.current = false;
			if (wasExpandedBeforeBizyRef.current) setIsExpanded(true);
			wasExpandedBeforeBizyRef.current = null;
		}
	}, [isPanelOpen, isFullscreen, isLargeDesktop, isMobile, isExpanded, setIsExpanded]);

	// Auto-collapse Bizy when a navbar panel (Leaderboard, Team, etc.) opens; restore when it closes
	const openNavbarPanel = useNavbarPanelsStore((s) => s.openPanel);
	const wasBizyOpenBeforeSheetRef = useRef(false);
	useEffect(() => {
		if (isMobile) return;
		if (openNavbarPanel) {
			if (useBizySidePanelStore.getState().isOpen) {
				wasBizyOpenBeforeSheetRef.current = true;
				useBizySidePanelStore.getState().close('navbar_collapse');
			}
		} else if (wasBizyOpenBeforeSheetRef.current) {
			wasBizyOpenBeforeSheetRef.current = false;
			useBizySidePanelStore.getState().open('navbar_restore');
		}
	}, [openNavbarPanel, isMobile]);

	// "/" toggles the Bizy panel (when not focused in an input/textarea/contenteditable)
	const handleKeyDown = useCallback((event: KeyboardEvent) => handleBizySlashKeyDown(event, () => togglePanel('keyboard')), [togglePanel]);

	useEffect(() => {
		if (!isBizyEnabledForUser || isMobile) return;
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isBizyEnabledForUser, isMobile, handleKeyDown]);

	return {
		isDesktop,
		isPanelOpen,
		isFullscreen,
		panelWidth,
		isResizing,
		togglePanel,
	};
}
